import React, { useState, useMemo } from 'react';
import { usePermissions } from '../../context/PermissionsContext.tsx';
import {
  Plus,
  Clock,
  CheckSquare,
  MessageSquare,
  Paperclip,
  MoreHorizontal,
  AlertCircle,
  CheckCircle2,
  Circle,
  Copy,
  Archive,
  Edit3,
  Palette,
  ArrowLeft,
  ArrowRight,
  Trash2,
  AlignLeft,
  Eye,
  Minimize2,
  Maximize2,
  ArrowUpDown,
  Tag,
  X,
  Check,
  MoveRight,
  Share2,
  SlidersHorizontal,
  Layers,
  Package,
  Users,
  Lock,
  RotateCcw,
} from 'lucide-react';
import { Task, TaskStatus, BoardList, CardLabel, User, canTransitionTask, checkTaskTransition } from '../../../shared/types.ts';
import { PriorityBadge } from '../common/Badge.tsx';
import { Avatar } from '../common/Avatar.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { RequestChangesModal } from '../tasks/RequestChangesModal.tsx';

export const TRELLO_LABEL_PALETTE: Array<{ color: string; name: string; bg: string; text: string; dot: string }> = [
  { color: 'emerald', name: 'Approved / Ready', bg: 'bg-emerald-500 text-white', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-500' },
  { color: 'amber', name: 'Attention / Review', bg: 'bg-amber-500 text-white', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500' },
  { color: 'orange', name: 'High Impact', bg: 'bg-orange-500 text-white', text: 'text-orange-700 dark:text-orange-300', dot: 'bg-orange-500' },
  { color: 'rose', name: 'Blocker / Bug', bg: 'bg-rose-500 text-white', text: 'text-rose-700 dark:text-rose-300', dot: 'bg-rose-500' },
  { color: 'purple', name: 'Strategy / UX', bg: 'bg-purple-500 text-white', text: 'text-purple-700 dark:text-purple-300', dot: 'bg-purple-500' },
  { color: 'blue', name: 'Engineering', bg: 'bg-blue-600 text-white', text: 'text-blue-700 dark:text-blue-300', dot: 'bg-blue-600' },
  { color: 'sky', name: 'Client Request', bg: 'bg-sky-500 text-white', text: 'text-sky-700 dark:text-sky-300', dot: 'bg-sky-500' },
  { color: 'pink', name: 'Marketing / Brand', bg: 'bg-pink-500 text-white', text: 'text-pink-700 dark:text-pink-300', dot: 'bg-pink-500' },
];

export const TRELLO_COVER_COLORS = [
  { id: 'none', label: 'None', className: 'bg-surface-200 dark:bg-surface-700', hex: '' },
  { id: '#10b981', label: 'Emerald', className: 'bg-emerald-500', hex: '#10b981' },
  { id: '#f59e0b', label: 'Amber', className: 'bg-amber-500', hex: '#f59e0b' },
  { id: '#f97316', label: 'Orange', className: 'bg-orange-500', hex: '#f97316' },
  { id: '#f43f5e', label: 'Rose', className: 'bg-rose-500', hex: '#f43f5e' },
  { id: '#8b5cf6', label: 'Purple', className: 'bg-purple-500', hex: '#8b5cf6' },
  { id: '#026fc1', label: 'Ocean Blue', className: 'bg-[#026fc1]', hex: '#026fc1' },
  { id: '#0ea5e9', label: 'Sky', className: 'bg-sky-500', hex: '#0ea5e9' },
  { id: '#ec4899', label: 'Pink', className: 'bg-pink-500', hex: '#ec4899' },
  { id: '#475569', label: 'Slate', className: 'bg-slate-600', hex: '#475569' },
];

export const LIST_DOT_COLORS = [
  { label: 'Amber', value: 'bg-amber-500' },
  { label: 'Blue', value: 'bg-brand-500' },
  { label: 'Purple', value: 'bg-purple-500' },
  { label: 'Emerald', value: 'bg-emerald-500' },
  { label: 'Rose', value: 'bg-rose-500' },
  { label: 'Sky', value: 'bg-sky-500' },
  { label: 'Slate', value: 'bg-slate-500' },
];

interface KanbanBoardProps {
  tasks: Task[];
  onTaskClick: (task: Task) => void;
  onUpdateTaskStatus: (taskId: string, newStatus: TaskStatus, listId?: string | null) => void;
  onQuickCreateTask: (status: TaskStatus, title: string, listId?: string | null) => void;
  projectId?: string;
  lists?: BoardList[];
  members?: User[];
  onListsChange?: (lists: BoardList[]) => void;
  onRefreshTasks?: () => void;
  hideUserFilter?: boolean;
  /** Remove the whole Overdue section: the Overdue lane and every overdue ticket */
  hideOverdue?: boolean;
  /** Remove the whole Completed section: the Completed lane and every completed ticket */
  hideCompleted?: boolean;
}

const DEFAULT_COLUMNS: Array<{ id: string; title: string; statusKey: TaskStatus; color: string; isOverdue?: boolean }> = [
  { id: 'col-todo', title: 'To Do', statusKey: 'todo', color: 'bg-amber-500' },
  { id: 'col-in_progress', title: 'In Progress', statusKey: 'in_progress', color: 'bg-brand-500' },
  { id: 'col-in_review', title: 'In Review', statusKey: 'in_review', color: 'bg-purple-500' },
  { id: 'col-done', title: 'Completed', statusKey: 'done', color: 'bg-emerald-500' },
];

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  tasks,
  onTaskClick,
  onUpdateTaskStatus,
  onQuickCreateTask,
  projectId,
  lists,
  members,
  onListsChange,
  onRefreshTasks,
  hideUserFilter = false,
  hideOverdue = false,
  hideCompleted = false,
}) => {
  const { token, user } = useAuth();

  // Quick Add Card state (supports top or bottom of any column)
  const [quickInputCol, setQuickInputCol] = useState<string | null>(null);
  const [quickTitle, setQuickTitle] = useState('');

  // Drag & Drop state (cards & lists)
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dragOverColId, setDragOverColId] = useState<string | null>(null);
  const [draggedListId, setDraggedListId] = useState<string | null>(null);

  // Trello Label text expand/collapse toggle across board
  const [labelsExpanded, setLabelsExpanded] = useState<boolean>(true);

  // Show/hide virtual Overdue lane (default true so dedicated Overdue column is always visible)
  const [showOverdueLane, setShowOverdueLane] = useState<boolean>(true);

  // Mobile column selector
  const [activeMobileCol, setActiveMobileCol] = useState<string>('all');

  // List management states
  const [openListMenuId, setOpenListMenuId] = useState<string | null>(null);
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [editingListTitle, setEditingListTitle] = useState('');
  const [editingWipListId, setEditingWipListId] = useState<string | null>(null);
  const [editingWipValue, setEditingWipValue] = useState<string>('0');
  const [collapsedListIds, setCollapsedListIds] = useState<Set<string>>(new Set());
  const [listSortBy, setListSortBy] = useState<Record<string, 'default' | 'due_date' | 'priority' | 'title' | 'newest'>>({});

  // Add New List state
  const [isAddingList, setIsAddingList] = useState(false);
  const [newListTitle, setNewListTitle] = useState('');
  const [newListColor, setNewListColor] = useState('bg-brand-500');
  const [newListStatusKey, setNewListStatusKey] = useState<TaskStatus>('todo');

  // Card Quick Context Menu & Inline Edit state
  const [openCardMenuId, setOpenCardMenuId] = useState<string | null>(null);
  const [inlineEditCardId, setInlineEditCardId] = useState<string | null>(null);
  const [inlineEditTitle, setInlineEditTitle] = useState('');
  const [cardMenuSubView, setCardMenuSubView] = useState<'main' | 'labels' | 'cover' | 'move' | 'dates'>('main');
  const [copiedCardId, setCopiedCardId] = useState<string | null>(null);

  // Review & Request Changes Modal state
  const [isRequestChangesModalOpen, setIsRequestChangesModalOpen] = useState(false);
  const [pendingRequestChangesTask, setPendingRequestChangesTask] = useState<Task | null>(null);
  const [isSubmittingReviewAction, setIsSubmittingReviewAction] = useState(false);

  // User authorization checks
  const isWorkspaceAdmin = user?.role === 'admin';
  const currentMember = members?.find((m) => m.id === user?.id);
  const isProjectLead =
    isWorkspaceAdmin ||
    user?.role === 'team_lead' ||
    user?.role === 'manager' ||
    (currentMember as any)?.projectRole === 'lead' ||
    (currentMember as any)?.projectRole === 'team_lead';
  // What each role may do is set on the Roles & Access screen: a viewer / guest is read-only unless
  // their role was given "Edit & move tasks"
  const { can } = usePermissions();
  const canCreateTask = can('task.create');
  const canEditTaskRole = can('task.edit');
  const isViewer = (user?.role === 'viewer' && !canEditTaskRole) || (currentMember as any)?.projectRole === 'viewer';
  const isGuest = (user?.role === 'guest' && !canEditTaskRole) || (currentMember as any)?.projectRole === 'guest';

  // Leading this very project lets someone review its tasks; what a workspace role may do is
  // decided by the role permissions (Roles & Access screen) through `role`
  const isLeadOfThisProject =
    (currentMember as any)?.projectRole === 'lead' || (currentMember as any)?.projectRole === 'team_lead';
  const currentUserForPermissions = useMemo(() => ({
    id: user?.id || '',
    workspaceRole: isWorkspaceAdmin ? 'ADMIN' : undefined,
    projectRole: isLeadOfThisProject ? 'TEAM_LEAD' : (isViewer ? 'VIEWER' : isGuest ? 'GUEST' : 'MEMBER'),
    role: user?.role,
  }), [user?.id, user?.role, isWorkspaceAdmin, isLeadOfThisProject, isViewer, isGuest]);

  const canApproveTask = (task?: Task | null): boolean => {
    if (!task) return false;
    return canTransitionTask(currentUserForPermissions, task, 'COMPLETED');
  };


  const handleQuickApprove = async (task: Task) => {
    if (!token) return;
    try {
      const res = await fetch(`/api/tasks/${task.id}/approve`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      });
      if (res.ok) {
        onRefreshTasks?.();
        onUpdateTaskStatus(task.id, 'done');
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to approve task.');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenRequestChanges = (task: Task) => {
    setPendingRequestChangesTask(task);
    setIsRequestChangesModalOpen(true);
  };

  const handleRequestChangesSubmit = async (reason: string) => {
    if (!pendingRequestChangesTask || !token) return;
    setIsSubmittingReviewAction(true);
    try {
      const res = await fetch(`/api/tasks/${pendingRequestChangesTask.id}/request-changes`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ rejectionReason: reason }),
      });
      if (res.ok) {
        onRefreshTasks?.();
        onUpdateTaskStatus(pendingRequestChangesTask.id, 'in_progress');
        setIsRequestChangesModalOpen(false);
        setPendingRequestChangesTask(null);
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to send the task for rework.');
      }
    } catch (err: any) {
      alert(err?.message || 'Failed to send the task for rework.');
    } finally {
      setIsSubmittingReviewAction(false);
    }
  };

  // User-wise task filter state ('all' | '__unassigned__' | userId)
  const [selectedUserId, setSelectedUserId] = useState<string>('all');

  // Feature filtering state (Item 17: Featurewise display of boards)
  const [selectedFeatureId, setSelectedFeatureId] = useState<string>('all');

  // Extract distinct assignees/users from tasks or explicit board members
  const availableUsers = useMemo(() => {
    const userMap = new Map<string, { id: string; fullName: string; avatarUrl?: string; count: number }>();
    let unassignedCount = 0;

    // Seed with project members if provided
    if (members && members.length > 0) {
      members.forEach((m) => {
        if (m && m.id) {
          userMap.set(m.id, {
            id: m.id,
            fullName: m.fullName || 'Member',
            avatarUrl: m.avatarUrl,
            count: 0,
          });
        }
      });
    }

    // Tally up counts and incorporate any assignees from tasks
    tasks.forEach((t) => {
      const taskAssignees = (t.assignees && t.assignees.length > 0)
        ? t.assignees
        : t.assigneeId
        ? [{ id: t.assigneeId, fullName: t.assigneeName || 'Assignee', avatarUrl: t.assigneeAvatar }]
        : [];

      if (taskAssignees.length === 0) {
        unassignedCount += 1;
      } else {
        taskAssignees.forEach((a: any) => {
          if (!a || !a.id) return;
          const existing = userMap.get(a.id);
          if (existing) {
            existing.count += 1;
            if (!existing.avatarUrl && a.avatarUrl) existing.avatarUrl = a.avatarUrl;
          } else {
            userMap.set(a.id, {
              id: a.id,
              fullName: a.fullName || 'Member',
              avatarUrl: a.avatarUrl,
              count: 1,
            });
          }
        });
      }
    });

    return {
      list: Array.from(userMap.values()),
      unassignedCount,
    };
  }, [tasks, members]);

  // Extract distinct features from tasks
  const availableFeatures = useMemo(() => {
    const map = new Map<string, { id: string; name: string; code?: string; count: number }>();
    let unassignedCount = 0;
    tasks.forEach((t) => {
      if (t.featureId) {
        const existing = map.get(t.featureId);
        if (existing) {
          existing.count += 1;
        } else {
          map.set(t.featureId, {
            id: t.featureId,
            name: t.featureName || 'Feature',
            code: t.featureCode,
            count: 1,
          });
        }
      } else {
        unassignedCount += 1;
      }
    });
    return {
      list: Array.from(map.values()),
      unassignedCount,
    };
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      // 1. Feature filter
      if (selectedFeatureId !== 'all') {
        if (selectedFeatureId === 'unassigned') {
          if (t.featureId) return false;
        } else if (t.featureId !== selectedFeatureId) {
          return false;
        }
      }

      // 2. User-wise filter
      if (!hideUserFilter && selectedUserId !== 'all') {
        if (selectedUserId === '__unassigned__') {
          const hasAssignee = Boolean(t.assigneeId || (t.assignees && t.assignees.length > 0));
          if (hasAssignee) return false;
        } else {
          const isAssigned =
            t.assigneeId === selectedUserId ||
            (t.assignees && t.assignees.some((a) => a.id === selectedUserId));
          if (!isAssigned) return false;
        }
      }

      return true;
    });
  }, [tasks, selectedFeatureId, selectedUserId, hideUserFilter]);

  const getAuthHeaders = (extra: Record<string, string> = {}) => ({
    Authorization: `Bearer ${token || ''}`,
    ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
    ...extra,
  });

  const isOverdue = (dueDateStr?: string | null) => {
    if (!dueDateStr) return false;
    const nowStr = new Date().toISOString().split('T')[0];
    return String(dueDateStr).slice(0, 10) < nowStr;
  };

  const isDueToday = (dueDateStr?: string | null) => {
    if (!dueDateStr) return false;
    const nowStr = new Date().toISOString().split('T')[0];
    return String(dueDateStr).slice(0, 10) === nowStr;
  };

  // Build effective columns from board lists or default columns
  const effectiveColumns = useMemo(() => {
    const baseCols =
      lists && lists.length > 0
        ? lists.map((l) => ({
            id: l.id,
            title: l.title,
            statusKey: (l.statusKey || 'todo') as TaskStatus,
            color: l.color || 'bg-brand-500',
            wipLimit: l.wipLimit || 0,
            isCollapsed: Boolean(l.isCollapsed) || collapsedListIds.has(l.id),
            isCustomList: true,
          }))
        : DEFAULT_COLUMNS.map((c) => ({
            ...c,
            wipLimit: 0,
            isCollapsed: collapsedListIds.has(c.id),
            isCustomList: false,
          }));

    const visibleBaseCols = hideCompleted
      ? baseCols.filter((c) => c.statusKey !== 'done' && (c.statusKey as string) !== 'completed')
      : baseCols;

    if (showOverdueLane && !hideOverdue) {
      return [
        {
          id: 'overdue',
          title: 'Overdue',
          statusKey: 'in_progress' as TaskStatus,
          color: 'bg-rose-500',
          wipLimit: 0,
          isCollapsed: collapsedListIds.has('overdue'),
          isCustomList: false,
          isOverdue: true,
        },
        ...visibleBaseCols,
      ];
    }
    return visibleBaseCols;
  }, [lists, collapsedListIds, showOverdueLane, hideOverdue, hideCompleted, projectId, tasks]);

  // Filter tasks belonging to a specific column
  const getColumnTasks = (col: any): Task[] => {
    const filtered = filteredTasks.filter((t) => {
      const taskIsOverdue =
        (t.status === 'overdue' || isOverdue(t.dueDate)) &&
        t.status !== 'done' &&
        t.status !== 'completed' &&
        t.status !== 'in_review';

      if (hideCompleted && (t.status === 'done' || (t.status as string) === 'completed')) {
        return false;
      }

      if (col.isOverdue) {
        return taskIsOverdue;
      }

      // The Overdue lane is only informational: an overdue ticket always stays in its own status column,
      // even when the lane is hidden

      const normalizedStatus = t.status === 'completed' ? 'done' : t.status;

      // The task's status decides its column, so the Boards screen and the Workspace always agree.
      // A saved list only picks between several columns that share the same status.
      const statusCols = effectiveColumns.filter(
        (c) => !c.isOverdue && (c.statusKey === normalizedStatus || c.statusKey === t.status)
      );
      if (statusCols.length > 0) {
        const home = statusCols.find((c) => c.id === t.listId) || statusCols[0];
        return home.id === col.id;
      }

      // No column has this status: fall back to the saved list
      if (t.listId && effectiveColumns.some((c) => c.id === t.listId)) {
        return t.listId === col.id;
      }
      const firstMatchingCol = effectiveColumns.find((c) => !c.isOverdue && (c.statusKey === normalizedStatus || c.statusKey === t.status));
      if (firstMatchingCol) {
        return firstMatchingCol.id === col.id;
      }
      if (col.statusKey === 'todo' && (t.status === 'todo' || t.status === 'backlog')) {
        const firstTodoCol = effectiveColumns.find((c) => !c.isOverdue && c.statusKey === 'todo');
        return firstTodoCol?.id === col.id;
      }
      return t.status === col.statusKey || normalizedStatus === col.statusKey;
    });

    const sortMode = listSortBy[col.id] || 'default';
    const sorted = [...filtered];
    if (sortMode === 'due_date') {
      sorted.sort((a, b) => (a.dueDate || '9999-99-99').localeCompare(b.dueDate || '9999-99-99'));
    } else if (sortMode === 'priority') {
      const rank: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };
      sorted.sort((a, b) => (rank[a.priority] ?? 2) - (rank[b.priority] ?? 2));
    } else if (sortMode === 'title') {
      sorted.sort((a, b) => a.title.localeCompare(b.title));
    } else if (sortMode === 'newest') {
      sorted.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    } else {
      sorted.sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
    }
    return sorted;
  };

  // Card Drag & Drop handlers
  const handleCardDragStart = (e: React.DragEvent, taskId: string) => {
    e.stopPropagation();
    e.dataTransfer.setData('text/plain', taskId);
    e.dataTransfer.setData('application/x-kanban-card', taskId);
    e.dataTransfer.effectAllowed = 'move';
    // Changing the card's look inside dragstart can make browsers cancel the drag, so wait a tick
    setTimeout(() => setDraggedTaskId(taskId), 0);
  };

  // Dropping outside any column (or pressing Esc) ends the drag without a drop event
  const handleCardDragEnd = () => {
    setDraggedTaskId(null);
    setDragOverColId(null);
  };

  const handleColumnDragOver = (e: React.DragEvent, colId: string) => {
    e.preventDefault();
    if (dragOverColId !== colId) {
      setDragOverColId(colId);
    }
  };

  const handleColumnDrop = async (e: React.DragEvent, col: any, targetCardIndex?: number) => {
    e.preventDefault();
    setDragOverColId(null);

    // Check if dragging a list
    const listDragId = e.dataTransfer.getData('application/x-kanban-list') || draggedListId;
    if (listDragId && !draggedTaskId && projectId && lists && onListsChange) {
      if (listDragId !== col.id && !col.isOverdue) {
        const currentIds = lists.map((l) => l.id);
        const fromIdx = currentIds.indexOf(listDragId);
        const toIdx = currentIds.indexOf(col.id);
        if (fromIdx !== -1 && toIdx !== -1) {
          const reordered = [...lists];
          const [moved] = reordered.splice(fromIdx, 1);
          reordered.splice(toIdx, 0, moved);
          const updated = reordered.map((l, idx) => ({ ...l, orderIndex: idx }));
          onListsChange(updated);
          await fetch(`/api/projects/${projectId}/lists/reorder`, {
            method: 'PUT',
            headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ orderedListIds: updated.map((l) => l.id) }),
          }).catch(() => {});
        }
      }
      setDraggedListId(null);
      return;
    }

    // Dragging a card
    const taskId = e.dataTransfer.getData('application/x-kanban-card') || e.dataTransfer.getData('text/plain') || draggedTaskId;
    if (taskId) {
      const draggedTask = tasks.find((t) => t.id === taskId);
      if (!draggedTask) {
        setDraggedTaskId(null);
        return;
      }

      const targetStatus: TaskStatus = col.isOverdue ? 'in_progress' : col.statusKey;
      const targetListId = col.isCustomList ? col.id : null;

      // Rule 4: In Review -> In Progress by reviewer requires mandatory rejection comment modal
      const isPrivilegedReviewer = canApproveTask(draggedTask);
      if (draggedTask.status === 'in_review' && targetStatus === 'in_progress' && isPrivilegedReviewer && draggedTask.assigneeId !== user?.id) {
        setPendingRequestChangesTask(draggedTask);
        setIsRequestChangesModalOpen(true);
        setDraggedTaskId(null);
        return;
      }

      // Validate status transition against the 5 RBAC engine rules
      const check = checkTaskTransition(currentUserForPermissions, draggedTask, targetStatus);
      if (!check.allowed) {
        alert(check.error || 'Workflow Gated: You do not have permission to perform this task status transition.');
        setDraggedTaskId(null);
        return;
      }


      onUpdateTaskStatus(taskId, targetStatus, targetListId);

      // Persist order and listId to backend
      if (token) {
        const colTasks = getColumnTasks(col).filter((t) => t.id !== taskId);
        const draggedTask = tasks.find((t) => t.id === taskId);
        if (draggedTask) {
          const insertAt = targetCardIndex !== undefined ? targetCardIndex : colTasks.length;
          colTasks.splice(insertAt, 0, { ...draggedTask, status: targetStatus, listId: targetListId });
          const updates = colTasks.map((t, idx) => ({
            id: t.id,
            status: t.id === taskId ? targetStatus : t.status,
            listId: t.id === taskId ? targetListId : (t.listId ?? undefined),
            orderIndex: idx,
          }));
          fetch('/api/tasks/reorder', {
            method: 'POST',
            headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ updates }),
          }).catch(() => {});
        }
      }
    }
    setDraggedTaskId(null);
  };

  // Quick Add Card submission (supports multiline paste -> multiple cards!)
  const handleQuickSubmit = async (col: any) => {
    if (!quickTitle.trim()) return;
    const targetStatus: TaskStatus = col.isOverdue ? 'todo' : col.statusKey;
    const targetListId = col.isCustomList ? col.id : null;

    const lines = quickTitle
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    setQuickTitle('');
    setQuickInputCol(null);

    for (const line of lines) {
      onQuickCreateTask(targetStatus, line, targetListId);
    }
  };

  // List actions
  const handleAddList = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newListTitle.trim() || !projectId || !token) return;
    const title = newListTitle.trim();
    setNewListTitle('');
    setIsAddingList(false);

    try {
      const res = await fetch(`/api/projects/${projectId}/lists`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          title,
          statusKey: newListStatusKey,
          color: newListColor,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.lists && onListsChange) {
          onListsChange(data.lists);
        }
      }
    } catch (err) {
      console.error('Failed to add list:', err);
    }
  };

  const handleRenameList = async (listId: string) => {
    if (!editingListTitle.trim() || !projectId || !token) {
      setEditingListId(null);
      return;
    }
    const newTitle = editingListTitle.trim();
    setEditingListId(null);
    if (lists && onListsChange) {
      onListsChange(lists.map((l) => (l.id === listId ? { ...l, title: newTitle } : l)));
    }
    await fetch(`/api/projects/${projectId}/lists/${listId}`, {
      method: 'PUT',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ title: newTitle }),
    }).catch(() => {});
  };

  const handleUpdateListMeta = async (listId: string, updates: Partial<BoardList>) => {
    if (!projectId || !token) return;
    if (lists && onListsChange) {
      onListsChange(lists.map((l) => (l.id === listId ? { ...l, ...updates } : l)));
    }
    await fetch(`/api/projects/${projectId}/lists/${listId}`, {
      method: 'PUT',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(updates),
    }).catch(() => {});
  };

  const handleMoveList = async (listId: string, direction: 'left' | 'right') => {
    if (!lists || !projectId || !onListsChange) return;
    const idx = lists.findIndex((l) => l.id === listId);
    if (idx === -1) return;
    const targetIdx = direction === 'left' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= lists.length) return;

    const reordered = [...lists];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(targetIdx, 0, moved);
    const updated = reordered.map((l, i) => ({ ...l, orderIndex: i }));
    onListsChange(updated);
    setOpenListMenuId(null);

    await fetch(`/api/projects/${projectId}/lists/reorder`, {
      method: 'PUT',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ orderedListIds: updated.map((l) => l.id) }),
    }).catch(() => {});
  };

  const handleArchiveAllInList = async (listId: string) => {
    if (!projectId || !token) return;
    if (!confirm('Move all cards in this list to the Bin / Archive?')) return;
    setOpenListMenuId(null);
    await fetch(`/api/projects/${projectId}/lists/${listId}/archive-cards`, {
      method: 'POST',
      headers: getAuthHeaders(),
    }).catch(() => {});
    onRefreshTasks?.();
  };

  const handleDeleteList = async (listId: string) => {
    if (!projectId || !token) return;
    if (!confirm('Delete this list? Any cards in this list will be preserved in To Do.')) return;
    setOpenListMenuId(null);
    const res = await fetch(`/api/projects/${projectId}/lists/${listId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.lists && onListsChange) onListsChange(data.lists);
      onRefreshTasks?.();
    }
  };

  // Card Quick Actions
  const handleToggleCardComplete = async (e: React.MouseEvent, task: Task) => {
    e.stopPropagation();
    if (isViewer || isGuest) {
      alert(isViewer ? 'Viewers have read-only access.' : 'Guests cannot change card status.');
      return;
    }
    const nextStatus: TaskStatus = task.status === 'done' ? 'in_progress' : 'done';
    if (nextStatus === 'done' && !canApproveTask(task)) {
      alert('Workflow Gated: Only Team Leads, Workspace Admins, or the task Assignor can approve tasks into Completed.');
      return;
    }
    if (task.status === 'done' && nextStatus !== 'done' && !isWorkspaceAdmin && !isProjectLead) {
      alert('Only Team Leads or Workspace Admins can reopen completed tasks.');
      return;
    }
    const doneCol = effectiveColumns.find((c) => !c.isOverdue && c.statusKey === nextStatus);
    onUpdateTaskStatus(task.id, nextStatus, doneCol?.isCustomList ? doneCol.id : null);
  };

  const handleDuplicateCard = async (e: React.MouseEvent, task: Task) => {
    e.stopPropagation();
    setOpenCardMenuId(null);
    if (!token) return;
    try {
      const res = await fetch(`/api/tasks/${task.id}/duplicate`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.task) {
          window.dispatchEvent(new CustomEvent('task-created', { detail: data.task }));
        }
        onRefreshTasks?.();
      }
    } catch (err) {
      console.error('Failed to duplicate card:', err);
    }
  };

  const handleArchiveCard = async (e: React.MouseEvent, task: Task) => {
    e.stopPropagation();
    setOpenCardMenuId(null);
    if (!token) return;
    try {
      const res = await fetch(`/api/tasks/${task.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        window.dispatchEvent(new CustomEvent('task-archived', { detail: { taskId: task.id, projectId: task.projectId } }));
        onRefreshTasks?.();
      }
    } catch (err) {
      console.error('Failed to archive card:', err);
    }
  };

  const handleUpdateCardQuickField = async (task: Task, updates: Partial<Task>) => {
    if (!token) return;
    try {
      const res = await fetch(`/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.task) {
          window.dispatchEvent(new CustomEvent('task-updated', { detail: data.task }));
        }
        onRefreshTasks?.();
      }
    } catch (err) {
      console.error('Failed to update card:', err);
    }
  };

  const handleToggleCardLabel = async (e: React.MouseEvent, task: Task, paletteItem: typeof TRELLO_LABEL_PALETTE[0]) => {
    e.stopPropagation();
    const current = Array.isArray(task.coloredLabels) ? task.coloredLabels : [];
    const exists = current.some((l) => l.color === paletteItem.color);
    const nextLabels: CardLabel[] = exists
      ? current.filter((l) => l.color !== paletteItem.color)
      : [...current, { id: `lbl-${paletteItem.color}`, name: paletteItem.name, color: paletteItem.color }];
    await handleUpdateCardQuickField(task, { coloredLabels: nextLabels });
  };

  const overdueTotalCount = tasks.filter((t) => isOverdue(t.dueDate) && t.status !== 'done').length;

  return (
    <div
      className="flex-1 min-h-0 flex flex-col gap-1 overflow-hidden"
      onClick={() => {
        setOpenListMenuId(null);
        setOpenCardMenuId(null);
      }}
    >
      {/* Mobile-only Column Selector Tab Bar & Overdue Toggle */}
      {!hideUserFilter ? (
        <div className="flex items-center justify-between gap-2 shrink-0">
          <div className="sm:hidden flex items-center space-x-1 overflow-x-auto no-scrollbar pb-0.5 px-0.5 touch-scroll">
            <button
              onClick={() => setActiveMobileCol('all')}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold shrink-0 transition-all ${
                activeMobileCol === 'all'
                  ? 'bg-[#121417] text-white dark:bg-white dark:text-black shadow-2xs'
                  : 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400'
              }`}
            >
              All Lists
            </button>
            {effectiveColumns.map((col) => {
              const count = getColumnTasks(col).length;
              const isSelected = activeMobileCol === col.id;
              return (
                <button
                  key={col.id}
                  onClick={() => setActiveMobileCol(col.id)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-bold shrink-0 flex items-center space-x-1 transition-all ${
                    isSelected
                      ? 'bg-brand-600 text-white shadow-2xs'
                      : 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${col.color}`} />
                  <span>{col.title}</span>
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                      isSelected ? 'bg-white/20 text-white' : 'bg-surface-200 dark:bg-surface-700'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* User-wise Filter Dropdown & Overdue Toggle */}
          <div className="ml-auto flex items-center space-x-2 shrink-0">
            {/* User-wise Filter Dropdown */}
            {!hideUserFilter && availableUsers.list.length > 0 && (
              <div className="flex items-center space-x-1.5 bg-surface-100/90 dark:bg-surface-800/90 rounded-lg px-2 py-0.5 border border-surface-200 dark:border-surface-700">
                <Users className="w-3 h-3 text-surface-500 shrink-0" />
                <span className="text-[10px] font-bold text-surface-500 uppercase tracking-wider hidden md:inline">
                  User:
                </span>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="bg-transparent text-[11px] font-semibold text-surface-800 dark:text-surface-200 focus:outline-none cursor-pointer py-0.5 pr-1 w-[160px] truncate"
                  title="Filter tasks by user"
                >
                  <option value="all">All Users ({availableUsers.list.length})</option>
                  {user && (
                    <option value={user.id}>
                      Assigned to Me ({tasks.filter((t) => t.assigneeId === user.id || (t.assignees && t.assignees.some((a) => a.id === user.id))).length})
                    </option>
                  )}
                  {availableUsers.unassignedCount > 0 && (
                    <option value="__unassigned__">
                      Unassigned ({availableUsers.unassignedCount})
                    </option>
                  )}
                  {availableUsers.list
                    .filter((u) => !user || u.id !== user.id)
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.fullName} ({u.count})
                      </option>
                    ))}
                </select>
                {selectedUserId !== 'all' && (
                  <button
                    type="button"
                    onClick={() => setSelectedUserId('all')}
                    className="p-0.5 rounded text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 transition-colors"
                    title="Clear user filter"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}

            {/* Overdue Lane Toggle Button */}
            {projectId && overdueTotalCount > 0 && (
              <button
                type="button"
                onClick={() => setShowOverdueLane((prev) => !prev)}
                className={`px-2 py-0.5 rounded-lg text-[10px] font-bold flex items-center space-x-1 border transition-all ${
                  showOverdueLane
                    ? 'bg-rose-500 text-white border-rose-500 shadow-2xs'
                    : 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20 hover:bg-rose-500/20'
                }`}
              >
                <AlertCircle className="w-3 h-3" />
                <span>
                  {showOverdueLane ? 'Hide Overdue Lane' : `Show Overdue Lane (${overdueTotalCount})`}
                </span>
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="sm:hidden flex items-center space-x-1 overflow-x-auto no-scrollbar pb-0.5 px-0.5 touch-scroll shrink-0">
          <button
            onClick={() => setActiveMobileCol('all')}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold shrink-0 transition-all ${
              activeMobileCol === 'all'
                ? 'bg-[#121417] text-white dark:bg-white dark:text-black shadow-2xs'
                : 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400'
            }`}
          >
            All Lists
          </button>
          {effectiveColumns.map((col) => {
            const count = getColumnTasks(col).length;
            const isSelected = activeMobileCol === col.id;
            return (
              <button
                key={col.id}
                onClick={() => setActiveMobileCol(col.id)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold shrink-0 flex items-center space-x-1 transition-all ${
                  isSelected
                    ? 'bg-brand-600 text-white shadow-2xs'
                    : 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${col.color}`} />
                <span>{col.title}</span>
                <span
                  className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                    isSelected ? 'bg-white/20 text-white' : 'bg-surface-200 dark:bg-surface-700'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Horizontal Board Lists Canvas (Fits above the fold with internal per-column vertical scroll).
          The wheel only scrolls vertically; sideways scrolling is Shift+wheel, a trackpad swipe or the scrollbar,
          so reaching the end of a column never moves the board sideways by itself. */}
      <div
        onWheel={(e) => {
          // The card columns block scroll chaining (overscroll-contain), so the board is moved here, and only
          // for a deliberate sideways gesture: Shift+wheel, or a clearly horizontal swipe. The small sideways
          // residue of a vertical trackpad/wheel gesture is ignored instead of sliding the board.
          if (!(e.target as HTMLElement).closest('[data-kanban-col-scroll="true"]')) return;
          const sideways = e.shiftKey
            ? e.deltaX || e.deltaY
            : Math.abs(e.deltaX) > Math.abs(e.deltaY) * 2
            ? e.deltaX
            : 0;
          if (sideways) e.currentTarget.scrollLeft += sideways;
        }}
        className="flex-1 min-h-0 flex items-stretch gap-2.5 sm:gap-3 overflow-x-auto overflow-y-hidden overscroll-x-contain thin-scrollbar pb-1.5 pt-0.5 touch-scroll"
      >
        {effectiveColumns.map((col, colIndex) => {
          const colTasks = getColumnTasks(col);
          const isOverWip = col.wipLimit > 0 && colTasks.length > col.wipLimit;
          const isAtWip = col.wipLimit > 0 && colTasks.length === col.wipLimit;

          // Collapsed List View (Vertical Strip)
          if (col.isCollapsed) {
            return (
              <div
                key={col.id}
                onClick={() => {
                  setCollapsedListIds((prev) => {
                    const next = new Set(prev);
                    next.delete(col.id);
                    return next;
                  });
                  if (col.isCustomList) handleUpdateListMeta(col.id, { isCollapsed: false });
                }}
                title={`Click to expand "${col.title}"`}
                className="w-10 shrink-0 h-full bg-[#f4f5f7] dark:bg-[#121417]/90 hover:bg-surface-200/80 dark:hover:bg-surface-800 rounded-xl py-3 px-1.5 border border-[#d5d8de]/60 dark:border-surface-800 cursor-pointer flex flex-col items-center justify-between transition-all shadow-2xs group"
              >
                <div className="flex flex-col items-center space-y-2">
                  <Maximize2 className="w-3.5 h-3.5 text-surface-400 group-hover:text-brand-500 transition-colors" />
                  <span className={`w-2 h-2 rounded-full ${col.color}`} />
                  <span className="text-[10px] font-bold px-1.5 py-0.2 rounded-full bg-surface-200 dark:bg-surface-800 text-surface-700 dark:text-surface-300">
                    {colTasks.length}
                  </span>
                </div>
                <div
                  className="font-bold text-[11px] text-surface-700 dark:text-surface-200 uppercase tracking-wider whitespace-nowrap"
                  style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                >
                  {col.title}
                </div>
                <div />
              </div>
            );
          }

          return (
            <div
              key={col.id}
              draggable={Boolean(projectId && col.isCustomList)}
              onDragStart={(e) => {
                if (projectId && col.isCustomList) {
                  e.dataTransfer.setData('application/x-kanban-list', col.id);
                  setDraggedListId(col.id);
                }
              }}
              onDragOver={(e) => handleColumnDragOver(e, col.id)}
              onDragLeave={() => {
                if (dragOverColId === col.id) setDragOverColId(null);
              }}
              onDrop={(e) => handleColumnDrop(e, col)}
              className={`w-[78vw] sm:w-[262px] md:w-[272px] shrink-0 h-full max-h-full flex flex-col rounded-xl p-2 border transition-all shadow-2xs ${
                dragOverColId === col.id
                  ? 'bg-brand-50/70 dark:bg-brand-950/30 border-brand-500 ring-2 ring-brand-500/20'
                  : isOverWip
                  ? 'bg-rose-50/70 dark:bg-rose-950/20 border-rose-400 dark:border-rose-700'
                  : 'bg-[#f4f5f7] dark:bg-[#121417]/90 border-[#d5d8de]/60 dark:border-surface-800'
              } ${activeMobileCol !== 'all' && activeMobileCol !== col.id ? 'hidden sm:flex' : 'flex'}`}
            >
              {/* List Header */}
              <div className="flex items-center justify-between pb-1.5 px-1 relative shrink-0">
                <div className="flex items-center space-x-1.5 min-w-0 flex-1 mr-1">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${col.color}`} />

                  {editingListId === col.id ? (
                    <input
                      type="text"
                      autoFocus
                      value={editingListTitle}
                      onChange={(e) => setEditingListTitle(e.target.value)}
                      onBlur={() => handleRenameList(col.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameList(col.id);
                        if (e.key === 'Escape') setEditingListId(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="text-[11px] font-bold bg-white dark:bg-surface-800 border border-brand-500 rounded-md px-1.5 py-0.5 text-surface-900 dark:text-surface-100 focus:outline-none w-full"
                    />
                  ) : (
                    <span
                      onClick={(e) => {
                        if (projectId && col.isCustomList) {
                          e.stopPropagation();
                          setEditingListId(col.id);
                          setEditingListTitle(col.title);
                        }
                      }}
                      title={projectId && col.isCustomList ? 'Click to rename list' : col.title}
                      className={`font-bold text-[11px] text-surface-900 dark:text-surface-100 uppercase tracking-wider truncate ${
                        projectId && col.isCustomList
                          ? 'cursor-text hover:text-brand-600 dark:hover:text-brand-400'
                          : ''
                      }`}
                    >
                      {col.title}
                    </span>
                  )}

                  {col.statusKey === 'done' && !isWorkspaceAdmin && !isProjectLead && (
                    <span title="Approval gated: Only Team Leads, Admins, or task Assignor can approve into Completed" className="text-amber-500 shrink-0">
                      <Lock className="w-3 h-3" />
                    </span>
                  )}

                  {/* Count & WIP Limit Badge */}
                  <span
                    className={`text-[10px] font-bold px-1.5 py-0.2 rounded-full shrink-0 ${
                      isOverWip
                        ? 'bg-rose-500 text-white animate-pulse'
                        : isAtWip
                        ? 'bg-amber-500 text-white'
                        : 'bg-surface-200 dark:bg-surface-800 text-surface-600 dark:text-surface-400'
                    }`}
                    title={
                      col.wipLimit > 0
                        ? `Work-in-Progress Limit: ${colTasks.length} / ${col.wipLimit}`
                        : `${colTasks.length} cards`
                    }
                  >
                    {col.wipLimit > 0 ? `${colTasks.length}/${col.wipLimit}` : colTasks.length}
                  </span>
                </div>

                {/* List Header Controls */}
                <div className="flex items-center space-x-0.5 shrink-0">
                  {canCreateTask && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setQuickInputCol(col.id);
                      }}
                      title="Add a card to this list"
                      className="p-1 rounded-md text-surface-400 hover:text-surface-700 dark:hover:text-surface-200 hover:bg-surface-200 dark:hover:bg-surface-800 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCollapsedListIds((prev) => new Set(prev).add(col.id));
                      if (col.isCustomList) handleUpdateListMeta(col.id, { isCollapsed: true });
                    }}
                    title="Collapse list"
                    className="p-1 rounded-md text-surface-400 hover:text-surface-700 dark:hover:text-surface-200 hover:bg-surface-200 dark:hover:bg-surface-800 transition-colors"
                  >
                    <Minimize2 className="w-3 h-3" />
                  </button>

                  <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenListMenuId(openListMenuId === col.id ? null : col.id);
                      }}
                      title="List actions"
                      className="p-1 rounded-md text-surface-400 hover:text-surface-700 dark:hover:text-surface-200 hover:bg-surface-200 dark:hover:bg-surface-800 transition-colors"
                    >
                      <MoreHorizontal className="w-3.5 h-3.5" />
                    </button>

                    {/* Trello List Context Menu */}
                    {openListMenuId === col.id && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="absolute right-0 top-full mt-1 w-60 bg-white dark:bg-surface-850 border border-surface-200 dark:border-surface-700 rounded-2xl shadow-elevated z-30 py-2 text-xs divide-y divide-surface-100 dark:divide-surface-800"
                      >
                        <div className="px-3 py-1.5 flex items-center justify-between">
                          <span className="font-bold text-surface-500 uppercase text-[10px] tracking-wider">
                            List Actions
                          </span>
                          <button
                            onClick={() => setOpenListMenuId(null)}
                            className="text-surface-400 hover:text-surface-600"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        <div className="py-1">
                          {canCreateTask && (
                            <button
                              onClick={() => {
                                setOpenListMenuId(null);
                                setQuickInputCol(col.id);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-surface-50 dark:hover:bg-surface-800 flex items-center space-x-2 text-surface-700 dark:text-surface-200"
                            >
                              <Plus className="w-3.5 h-3.5 text-brand-500" />
                              <span>Add card...</span>
                            </button>
                          )}

                          {projectId && col.isCustomList && (
                            <>
                              <button
                                onClick={() => {
                                  setOpenListMenuId(null);
                                  setEditingListId(col.id);
                                  setEditingListTitle(col.title);
                                }}
                                className="w-full px-3 py-2 text-left hover:bg-surface-50 dark:hover:bg-surface-800 flex items-center space-x-2 text-surface-700 dark:text-surface-200"
                              >
                                <Edit3 className="w-3.5 h-3.5 text-surface-400" />
                                <span>Rename list...</span>
                              </button>

                              <button
                                onClick={() => {
                                  setEditingWipListId(editingWipListId === col.id ? null : col.id);
                                  setEditingWipValue(String(col.wipLimit || 0));
                                }}
                                className="w-full px-3 py-2 text-left hover:bg-surface-50 dark:hover:bg-surface-800 flex items-center justify-between text-surface-700 dark:text-surface-200"
                              >
                                <span className="flex items-center space-x-2">
                                  <SlidersHorizontal className="w-3.5 h-3.5 text-surface-400" />
                                  <span>Set WIP Limit</span>
                                </span>
                                <span className="text-[10px] font-bold text-brand-600">
                                  {col.wipLimit > 0 ? col.wipLimit : 'None'}
                                </span>
                              </button>

                              {editingWipListId === col.id && (
                                <div className="px-3 py-2 bg-surface-50 dark:bg-surface-800/60 flex items-center space-x-2">
                                  <input
                                    type="number"
                                    min="0"
                                    max="50"
                                    value={editingWipValue}
                                    onChange={(e) => setEditingWipValue(e.target.value)}
                                    className="w-16 px-2 py-1 text-xs rounded-lg border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-900"
                                  />
                                  <button
                                    onClick={() => {
                                      handleUpdateListMeta(col.id, {
                                        wipLimit: Number(editingWipValue) || 0,
                                      });
                                      setEditingWipListId(null);
                                      setOpenListMenuId(null);
                                    }}
                                    className="px-2.5 py-1 bg-brand-600 text-white rounded-lg text-[11px] font-bold"
                                  >
                                    Save
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Sort List By */}
                        <div className="py-1">
                          <div className="px-3 py-1 text-[10px] font-bold text-surface-400 uppercase">
                            Sort Cards By
                          </div>
                          {[
                            { id: 'default', label: 'Manual Board Order' },
                            { id: 'due_date', label: 'Due Date (Earliest)' },
                            { id: 'priority', label: 'Priority (Urgent First)' },
                            { id: 'newest', label: 'Created Date (Newest)' },
                            { id: 'title', label: 'Card Name (A–Z)' },
                          ].map((s) => (
                            <button
                              key={s.id}
                              onClick={() => {
                                setListSortBy((prev) => ({ ...prev, [col.id]: s.id as any }));
                                setOpenListMenuId(null);
                              }}
                              className="w-full px-3 py-1.5 text-left hover:bg-surface-50 dark:hover:bg-surface-800 flex items-center justify-between text-surface-700 dark:text-surface-200"
                            >
                              <span>{s.label}</span>
                              {(listSortBy[col.id] || 'default') === s.id && (
                                <Check className="w-3.5 h-3.5 text-brand-600" />
                              )}
                            </button>
                          ))}
                        </div>

                        {/* List Color Picker & Move / Archive */}
                        {projectId && col.isCustomList && (
                          <div className="py-1.5 px-3 space-y-2">
                            <div className="text-[10px] font-bold text-surface-400 uppercase">
                              List Accent Color
                            </div>
                            <div className="flex items-center space-x-1.5">
                              {LIST_DOT_COLORS.map((c) => (
                                <button
                                  key={c.value}
                                  onClick={() => handleUpdateListMeta(col.id, { color: c.value })}
                                  className={`w-5 h-5 rounded-full ${c.value} ${
                                    col.color === c.value ? 'ring-2 ring-offset-1 ring-brand-500' : ''
                                  }`}
                                  title={c.label}
                                />
                              ))}
                            </div>

                            <div className="flex items-center justify-between pt-1">
                              <button
                                disabled={colIndex === 0}
                                onClick={() => handleMoveList(col.id, 'left')}
                                className="px-2.5 py-1 rounded-lg bg-surface-100 dark:bg-surface-800 hover:bg-surface-200 disabled:opacity-40 flex items-center space-x-1 text-[11px] font-medium"
                              >
                                <ArrowLeft className="w-3 h-3" />
                                <span>Move Left</span>
                              </button>
                              <button
                                disabled={colIndex >= effectiveColumns.length - 1}
                                onClick={() => handleMoveList(col.id, 'right')}
                                className="px-2.5 py-1 rounded-lg bg-surface-100 dark:bg-surface-800 hover:bg-surface-200 disabled:opacity-40 flex items-center space-x-1 text-[11px] font-medium"
                              >
                                <span>Move Right</span>
                                <ArrowRight className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        )}

                        {projectId && col.isCustomList && (
                          <div className="py-1">
                            <button
                              onClick={() => handleArchiveAllInList(col.id)}
                              className="w-full px-3 py-2 text-left hover:bg-amber-50 dark:hover:bg-amber-950/30 flex items-center space-x-2 text-amber-600 dark:text-amber-400"
                            >
                              <Archive className="w-3.5 h-3.5" />
                              <span>Archive all cards in list</span>
                            </button>
                            <button
                              onClick={() => handleDeleteList(col.id)}
                              className="w-full px-3 py-2 text-left hover:bg-rose-50 dark:hover:bg-rose-950/30 flex items-center space-x-2 text-rose-600 dark:text-rose-400"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span>Delete list</span>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* WIP Warning Banner */}
              {isOverWip && (
                <div className="mb-1.5 px-2 py-1 rounded-lg bg-rose-500/10 border border-rose-500/20 text-[10px] font-semibold text-rose-600 dark:text-rose-400 flex items-center space-x-1 shrink-0">
                  <AlertCircle className="w-3 h-3 shrink-0" />
                  <span>Exceeds WIP limit of {col.wipLimit} cards</span>
                </div>
              )}

              {/* Quick Add Input Bar (Top) */}
              {quickInputCol === col.id && (
                <div className="mb-2 p-2 bg-white dark:bg-surface-900 rounded-xl shadow-subtle border border-brand-500/50 shrink-0">
                  <textarea
                    rows={2}
                    autoFocus
                    placeholder="Enter card title... (Paste multiple lines for multiple cards)"
                    value={quickTitle}
                    onChange={(e) => setQuickTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleQuickSubmit(col);
                      }
                      if (e.key === 'Escape') setQuickInputCol(null);
                    }}
                    className="w-full text-xs bg-transparent border-none focus:outline-none text-surface-900 dark:text-surface-100 placeholder-surface-400 resize-none"
                  />
                  <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-surface-100 dark:border-surface-800 text-[11px]">
                    <button
                      type="button"
                      onClick={() => setQuickInputCol(null)}
                      className="text-surface-400 hover:text-surface-600 px-1.5 py-0.5"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleQuickSubmit(col)}
                      className="px-2.5 py-0.5 bg-brand-600 text-white rounded-md font-semibold text-[11px] hover:bg-brand-700 shadow-2xs"
                    >
                      Add Card
                    </button>
                  </div>
                </div>
              )}

              {/* Task Cards Stream (Scrolls vertically within this column) */}
              <div
                data-kanban-col-scroll="true"
                className="flex-1 min-h-0 overflow-y-auto overscroll-contain thin-scrollbar [scrollbar-gutter:stable] space-y-1.5 pr-0.5 touch-scroll"
              >
                {colTasks.map((task, cardIdx) => {
                  const overdue = isOverdue(task.dueDate) && task.status !== 'done';
                  const dueToday = isDueToday(task.dueDate) && task.status !== 'done';
                  const isDone = task.status === 'done';
                  const isWatching =
                    user?.id && Array.isArray(task.watchers) && task.watchers.includes(user.id);
                  const allChecklistsDone =
                    (task.checklistCount || 0) > 0 &&
                    task.completedChecklistCount === task.checklistCount;

                  return (
                    <div
                      key={`${col.id}-${task.id}`}
                      draggable={!isViewer && !isGuest}
                      onDragStart={(e) => handleCardDragStart(e, task.id)}
                      onDragEnd={handleCardDragEnd}
                      onDrop={(e) => {
                        e.stopPropagation();
                        handleColumnDrop(e, col, cardIdx);
                      }}
                      onClick={() => onTaskClick(task)}
                      className={`relative bg-white dark:bg-[#181b20] rounded-xl shadow-2xs hover:shadow-sm border border-[#d5d8de]/60 dark:border-surface-700/60 ${
                        isViewer || isGuest ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'
                      } hover:border-brand-500/50 dark:hover:border-brand-400/50 transition-all group ${
                        openCardMenuId === task.id ? 'z-40' : 'z-0'
                      } ${draggedTaskId === task.id ? 'opacity-40 scale-[0.98]' : ''}`}
                    >
                      {/* Trello Card Cover Image or Color Bar */}
                      {task.coverImageUrl ? (
                        <div className="h-20 w-full overflow-hidden rounded-t-xl bg-surface-100 dark:bg-surface-800 border-b border-surface-100 dark:border-surface-800">
                          <img
                            src={task.coverImageUrl}
                            alt={task.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      ) : task.coverColor ? (
                        <div
                          className="h-2.5 w-full rounded-t-xl"
                          style={{ backgroundColor: task.coverColor }}
                        />
                      ) : null}

                      <div className="p-2.5">
                        {/* Colored Labels Row (Trello Expandable Pills) */}
                        {task.coloredLabels && task.coloredLabels.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-1">
                            {task.coloredLabels.map((lbl) => {
                              const pal =
                                TRELLO_LABEL_PALETTE.find((p) => p.color === lbl.color) ||
                                TRELLO_LABEL_PALETTE[5];
                              return (
                                <button
                                  key={lbl.id || lbl.color}
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setLabelsExpanded((prev) => !prev);
                                  }}
                                  title={`${lbl.name} (Click to toggle label text)`}
                                  className={`transition-all font-bold rounded ${pal.bg} ${
                                    labelsExpanded
                                      ? 'px-1.5 py-0 text-[9px] leading-tight'
                                      : 'w-7 h-1.5 rounded-full'
                                  }`}
                                >
                                  {labelsExpanded ? lbl.name : ''}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* Compact Top Metadata Row: Project Key + Code + Priority */}
                        <div className="flex items-center justify-between gap-1 mb-1 min-w-0">
                          <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
                            {task.projectName && !projectId && (
                              <span className="text-[9px] font-bold text-brand-600 dark:text-brand-400 tracking-wide uppercase truncate max-w-[90px] min-w-0">
                                {task.projectKey || task.projectName}
                              </span>
                            )}
                            {task.code && (
                              <span className="text-[9px] font-mono font-bold px-1 py-0.5 rounded bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-300 border border-surface-200 dark:border-surface-700 leading-tight shrink-0">
                                {task.code}
                              </span>
                            )}
                            <span className="shrink-0">
                              <PriorityBadge priority={task.priority} size="sm" />
                            </span>

                            {/* Status dropdown: shown on hover, right next to the priority (takes no room otherwise) */}
                            <div className="hidden group-hover:flex items-center shrink-0">
                              <select
                                value={task.status}
                                disabled={isViewer || isGuest}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const nextStatus = e.target.value as TaskStatus;
                                  if (isViewer || isGuest) {
                                    alert(isViewer ? 'Viewers have read-only access.' : 'Guests cannot change card status.');
                                    return;
                                  }
                                  if (nextStatus === 'done' && !canApproveTask(task)) {
                                    alert('Workflow Gated: Only Team Leads, Workspace Admins, or the task Assignor can approve tasks into Completed.');
                                    return;
                                  }
                                  if (task.status === 'in_review' && nextStatus === 'in_progress') {
                                    if (!canApproveTask(task)) {
                                      alert('Only people who can review tasks may send a task back from In Review (with feedback).');
                                      return;
                                    }
                                    setPendingRequestChangesTask(task);
                                    setIsRequestChangesModalOpen(true);
                                    return;
                                  }
                                  if (task.status === 'done' && nextStatus !== 'done' && !isWorkspaceAdmin && !isProjectLead) {
                                    alert('Only Team Leads or Workspace Admins can reopen completed tasks.');
                                    return;
                                  }
                                  const matchCol = effectiveColumns.find(
                                    (c) => !c.isOverdue && c.statusKey === nextStatus
                                  );
                                  onUpdateTaskStatus(
                                    task.id,
                                    nextStatus,
                                    matchCol?.isCustomList ? matchCol.id : null
                                  );
                                }}
                                aria-label="Move task status"
                                className="text-[9px] bg-surface-100 dark:bg-surface-700 text-surface-700 dark:text-surface-300 rounded border border-surface-200 dark:border-surface-600 py-0 px-1 focus:ring-0 cursor-pointer h-5 disabled:opacity-50 min-w-0 max-w-[96px]"
                              >
                                <option value="backlog">Backlog</option>
                                <option value="todo">To Do</option>
                                <option value="in_progress">In Progress</option>
                                <option value="in_review">In Review</option>
                                <option value="done" disabled={!canApproveTask(task)}>Completed {!canApproveTask(task) ? '(Locked)' : ''}</option>
                              </select>
                            </div>
                          </div>

                          <div className="flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0">
                            {/* Quick Card Context Menu Trigger */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCardMenuSubView('main');
                                setOpenCardMenuId(openCardMenuId === task.id ? null : task.id);
                              }}
                              title="Card quick actions"
                              className="p-0.5 rounded bg-surface-100 dark:bg-surface-800 hover:bg-surface-200 dark:hover:bg-surface-700 text-surface-600 dark:text-surface-300"
                            >
                              <MoreHorizontal className="w-3 h-3" />
                            </button>
                          </div>
                        </div>

                        {/* Card Title with 1-Click Complete Checkbox */}
                        <div className="mb-1">
                          {inlineEditCardId === task.id ? (
                            <div onClick={(e) => e.stopPropagation()} className="space-y-1">
                              <input
                                type="text"
                                autoFocus
                                value={inlineEditTitle}
                                onChange={(e) => setInlineEditTitle(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && inlineEditTitle.trim()) {
                                    handleUpdateCardQuickField(task, {
                                      title: inlineEditTitle.trim(),
                                    });
                                    setInlineEditCardId(null);
                                  }
                                  if (e.key === 'Escape') setInlineEditCardId(null);
                                }}
                                className="w-full text-xs font-semibold px-1.5 py-0.5 rounded border border-brand-500 bg-white dark:bg-surface-900 text-surface-900 dark:text-surface-100 focus:outline-none"
                              />
                              <div className="flex justify-end space-x-1">
                                <button
                                  onClick={() => setInlineEditCardId(null)}
                                  className="px-1.5 py-0.5 text-[10px] text-surface-500"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => {
                                    if (inlineEditTitle.trim()) {
                                      handleUpdateCardQuickField(task, {
                                        title: inlineEditTitle.trim(),
                                      });
                                    }
                                    setInlineEditCardId(null);
                                  }}
                                  className="px-1.5 py-0.5 text-[10px] bg-brand-600 text-white rounded font-bold"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start space-x-1.5">
                              <button
                                type="button"
                                onClick={(e) => handleToggleCardComplete(e, task)}
                                title={isDone ? 'Mark incomplete' : 'Mark complete'}
                                className={`mt-0.5 shrink-0 transition-all ${
                                  isDone
                                    ? 'text-emerald-500 opacity-100'
                                    : 'text-surface-300 hover:text-emerald-500 opacity-100 sm:opacity-0 sm:group-hover:opacity-100'
                                }`}
                              >
                                {isDone ? (
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                ) : (
                                  <Circle className="w-3.5 h-3.5" />
                                )}
                              </button>
                              <h4
                                className={`text-xs font-semibold leading-snug line-clamp-2 flex-1 ${
                                  isDone
                                    ? 'line-through text-surface-400 dark:text-surface-500'
                                    : 'text-surface-900 dark:text-surface-100'
                                }`}
                              >
                                {task.title}
                              </h4>
                            </div>
                          )}
                        </div>

                        {/* Plain Tags & Checklist Mini Progress Bar (when present) */}
                        {((task.tags && task.tags.length > 0) ||
                          (task.checklistCount !== undefined && task.checklistCount > 0)) && (
                          <div className="space-y-1 mb-1">
                            {task.tags && task.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {task.tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className="px-1 py-0.1 text-[9px] font-medium rounded bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-300"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}

                            {task.checklistCount !== undefined && task.checklistCount > 0 && (
                              <div>
                                <div className="flex items-center justify-between text-[9px] text-surface-400 mb-0.5">
                                  <span
                                    className={`flex items-center font-medium ${
                                      allChecklistsDone ? 'text-emerald-600 dark:text-emerald-400' : ''
                                    }`}
                                  >
                                    <CheckSquare className="w-2.5 h-2.5 mr-0.5" />
                                    Checklist
                                  </span>
                                  <span
                                    className={
                                      allChecklistsDone
                                        ? 'text-emerald-600 dark:text-emerald-400 font-bold'
                                        : ''
                                    }
                                  >
                                    {task.completedChecklistCount || 0}/{task.checklistCount}
                                  </span>
                                </div>
                                <div className="w-full bg-surface-100 dark:bg-surface-700 h-1 rounded-full overflow-hidden">
                                  <div
                                    className={`${
                                      allChecklistsDone ? 'bg-emerald-500' : 'bg-brand-500'
                                    } h-full rounded-full transition-all`}
                                    style={{
                                      width: `${Math.round(
                                        ((task.completedChecklistCount || 0) / task.checklistCount) *
                                          100
                                      )}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Review state sits down here so the top row only carries project, ticket and priority */}
                        {(col.isOverdue ||
                          task.status === 'in_review' ||
                          task.reviewStatus === 'awaiting_approval' ||
                          task.reviewStatus === 'changes_requested') && (
                          <div className="flex flex-wrap items-center gap-1 mb-1.5">
                            {/* Underlying status when rendered in the Overdue column */}
                            {col.isOverdue && (
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-300 border border-surface-200 dark:border-surface-700 leading-tight whitespace-nowrap">
                                {task.status === 'in_progress' ? 'In Progress' : task.status === 'todo' ? 'To Do' : task.status}
                              </span>
                            )}
                            {(task.status === 'in_review' || task.reviewStatus === 'awaiting_approval') && (
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30 flex items-center gap-1 leading-tight whitespace-nowrap">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse shrink-0" />
                                Awaiting Approval
                              </span>
                            )}
                            {task.reviewStatus === 'changes_requested' && (
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-500/30 flex items-center gap-1 leading-tight whitespace-nowrap">
                                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
                                Rework{task.revisionCount ? ` (${task.revisionCount})` : ''}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Card Footer: Due Date, Badges, Assignee */}
                        <div className="flex items-center justify-between pt-1 border-t border-surface-100 dark:border-surface-700/50 text-[10px]">
                          {/* Due Date Indicator (Click to edit date directly) */}
                          <div className="flex items-center space-x-1">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCardMenuSubView('dates');
                                setOpenCardMenuId(
                                  openCardMenuId === task.id && cardMenuSubView === 'dates'
                                    ? null
                                    : task.id
                                );
                              }}
                              title="Click to edit due date"
                              className={`flex items-center text-[10px] font-medium px-1 py-0.2 rounded transition-colors cursor-pointer ${
                                task.dueDate
                                  ? isDone
                                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20'
                                    : overdue
                                    ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20 hover:bg-rose-500/20'
                                    : dueToday
                                    ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 hover:bg-amber-500/20'
                                    : 'text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800'
                                  : 'text-surface-400 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-surface-100 dark:hover:bg-surface-800'
                              }`}
                            >
                              {task.dueDate ? (
                                <>
                                  {overdue && <AlertCircle className="w-2.5 h-2.5 mr-0.5" />}
                                  {!overdue && <Clock className="w-2.5 h-2.5 mr-0.5" />}
                                  {String(task.dueDate).slice(0, 10)}
                                </>
                              ) : (
                                <>
                                  <Clock className="w-2.5 h-2.5 mr-0.5 opacity-70" />
                                  <span>Set date</span>
                                </>
                              )}
                            </button>
                          </div>

                          {/* Meta counters & Assignee */}
                          <div className="flex items-center space-x-1.5">
                            {isWatching && (
                              <span title="You are watching this card" className="text-brand-500">
                                <Eye className="w-3 h-3" />
                              </span>
                            )}

                            {task.description && task.description.trim().length > 0 && (
                              <span title="This card has a description" className="text-surface-400">
                                <AlignLeft className="w-3 h-3" />
                              </span>
                            )}

                            {task.commentCount !== undefined && task.commentCount > 0 && (
                              <span className="flex items-center text-surface-400 text-[10px]">
                                <MessageSquare className="w-2.5 h-2.5 mr-0.5" />
                                {task.commentCount}
                              </span>
                            )}

                            {task.attachmentCount !== undefined && task.attachmentCount > 0 && (
                              <span className="flex items-center text-surface-400 text-[10px]">
                                <Paperclip className="w-2.5 h-2.5 mr-0.5" />
                                {task.attachmentCount}
                              </span>
                            )}

                            <div
                              className="flex items-center -space-x-1"
                              title={
                                task.assignorName
                                  ? `Assignee: ${task.assigneeName || 'Unassigned'} | Assignor: ${task.assignorName}`
                                  : `Assignee: ${task.assigneeName || 'Unassigned'}`
                              }
                            >
                              {task.assignorName && task.assignorId !== task.assigneeId && (
                                <Avatar
                                  src={task.assignorAvatar}
                                  name={task.assignorName}
                                  size="xs"
                                  className="ring-1 ring-white dark:ring-surface-900 opacity-80"
                                />
                              )}
                              <Avatar
                                src={task.assigneeAvatar}
                                name={task.assigneeName || 'Unassigned'}
                                size="xs"
                                className="ring-1 ring-white dark:ring-surface-900"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Quick Review / Approval Actions on Card */}
                        {(task.status === 'in_review' || task.reviewStatus === 'awaiting_approval') && canApproveTask(task) && (
                          <div
                            className="flex items-center gap-1.5 mt-2 pt-1.5 border-t border-surface-150 dark:border-surface-800"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() => handleQuickApprove(task)}
                              title="Approve task and move to Completed"
                              className="flex-1 min-w-0 h-7 px-2 text-[10px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-md shadow-2xs flex items-center justify-center gap-1 whitespace-nowrap transition-colors"
                            >
                              <CheckCircle2 className="w-3 h-3 shrink-0" />
                              <span className="truncate">Approve</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenRequestChanges(task)}
                              title="Send for rework and return to In Progress"
                              className="flex-1 min-w-0 h-7 px-2 text-[10px] font-bold bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/40 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800 rounded-md flex items-center justify-center gap-1 whitespace-nowrap transition-colors"
                            >
                              <RotateCcw className="w-3 h-3 shrink-0" />
                              <span className="truncate">Rework</span>
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Trello Card Quick Context Menu Popover */}
                      {openCardMenuId === task.id && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="absolute right-2 top-8 w-56 bg-white dark:bg-surface-850 border border-surface-200 dark:border-surface-700 rounded-2xl shadow-elevated z-30 py-1.5 text-xs divide-y divide-surface-100 dark:divide-surface-800"
                        >
                          {cardMenuSubView === 'main' && (
                            <>
                              <div className="px-3 py-1 flex items-center justify-between">
                                <span className="text-[10px] font-bold uppercase text-surface-400">
                                  Quick Card Actions
                                </span>
                                <button
                                  onClick={() => setOpenCardMenuId(null)}
                                  className="text-surface-400 hover:text-surface-600"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                              <div className="py-1">
                                <button
                                  onClick={() => {
                                    setOpenCardMenuId(null);
                                    setInlineEditCardId(task.id);
                                    setInlineEditTitle(task.title);
                                  }}
                                  className="w-full px-3 py-1.5 text-left hover:bg-surface-50 dark:hover:bg-surface-800 flex items-center space-x-2 text-surface-700 dark:text-surface-200"
                                >
                                  <Edit3 className="w-3.5 h-3.5 text-surface-400" />
                                  <span>Rename Title</span>
                                </button>
                                <button
                                  onClick={() => setCardMenuSubView('dates')}
                                  className="w-full px-3 py-1.5 text-left hover:bg-surface-50 dark:hover:bg-surface-800 flex items-center space-x-2 text-surface-700 dark:text-surface-200"
                                >
                                  <Clock className="w-3.5 h-3.5 text-amber-500" />
                                  <span>Edit Due Date</span>
                                </button>
                                <button
                                  onClick={() => setCardMenuSubView('labels')}
                                  className="w-full px-3 py-1.5 text-left hover:bg-surface-50 dark:hover:bg-surface-800 flex items-center space-x-2 text-surface-700 dark:text-surface-200"
                                >
                                  <Tag className="w-3.5 h-3.5 text-brand-500" />
                                  <span>Edit Labels</span>
                                </button>
                                <button
                                  onClick={() => setCardMenuSubView('cover')}
                                  className="w-full px-3 py-1.5 text-left hover:bg-surface-50 dark:hover:bg-surface-800 flex items-center space-x-2 text-surface-700 dark:text-surface-200"
                                >
                                  <Palette className="w-3.5 h-3.5 text-purple-500" />
                                  <span>Change Cover</span>
                                </button>
                                <button
                                  onClick={() => setCardMenuSubView('move')}
                                  className="w-full px-3 py-1.5 text-left hover:bg-surface-50 dark:hover:bg-surface-800 flex items-center space-x-2 text-surface-700 dark:text-surface-200"
                                >
                                  <MoveRight className="w-3.5 h-3.5 text-emerald-500" />
                                  <span>Move to List...</span>
                                </button>
                                <button
                                  onClick={(e) => handleDuplicateCard(e, task)}
                                  className="w-full px-3 py-1.5 text-left hover:bg-surface-50 dark:hover:bg-surface-800 flex items-center space-x-2 text-surface-700 dark:text-surface-200"
                                >
                                  <Copy className="w-3.5 h-3.5 text-surface-400" />
                                  <span>Duplicate Card</span>
                                </button>
                                <button
                                  onClick={() => {
                                    const url = `${window.location.origin}/?project=${task.projectId}&task=${task.id}`;
                                    navigator.clipboard.writeText(url);
                                    setCopiedCardId(task.id);
                                    setTimeout(() => {
                                      setCopiedCardId(null);
                                      setOpenCardMenuId(null);
                                    }, 1000);
                                  }}
                                  className="w-full px-3 py-1.5 text-left hover:bg-surface-50 dark:hover:bg-surface-800 flex items-center space-x-2 text-surface-700 dark:text-surface-200"
                                >
                                  <Share2 className="w-3.5 h-3.5 text-surface-400" />
                                  <span>{copiedCardId === task.id ? 'Link Copied!' : 'Copy Card Link'}</span>
                                </button>
                              </div>
                              <div className="py-1">
                                <button
                                  onClick={(e) => handleArchiveCard(e, task)}
                                  className="w-full px-3 py-1.5 text-left hover:bg-amber-50 dark:hover:bg-amber-950/30 flex items-center space-x-2 text-amber-600 dark:text-amber-400"
                                >
                                  <Archive className="w-3.5 h-3.5" />
                                  <span>Move to Bin / Archive</span>
                                </button>
                              </div>
                            </>
                          )}

                          {cardMenuSubView === 'dates' && (
                            <div className="p-2.5 space-y-2">
                              <div className="flex items-center justify-between pb-1">
                                <button onClick={() => setCardMenuSubView('main')} className="text-[11px] text-brand-600 font-bold">
                                  ← Back
                                </button>
                                <span className="text-[10px] font-bold uppercase text-surface-400">Due Date</span>
                              </div>
                              <input
                                type="date"
                                value={task.dueDate ? task.dueDate.slice(0, 10) : ''}
                                onChange={(e) => {
                                  handleUpdateCardQuickField(task, { dueDate: e.target.value || null });
                                  setOpenCardMenuId(null);
                                }}
                                className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 text-surface-900 dark:text-surface-100 cursor-pointer"
                              />
                              <div className="grid grid-cols-2 gap-1 pt-0.5">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const todayStr = new Date().toISOString().split('T')[0];
                                    handleUpdateCardQuickField(task, { dueDate: todayStr });
                                    setOpenCardMenuId(null);
                                  }}
                                  className="px-2 py-1 text-[10px] font-semibold rounded-md bg-surface-100 dark:bg-surface-800 hover:bg-brand-50 dark:hover:bg-brand-500/15 hover:text-brand-600 text-surface-600 dark:text-surface-300"
                                >
                                  Today
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const d = new Date();
                                    d.setDate(d.getDate() + 1);
                                    handleUpdateCardQuickField(task, { dueDate: d.toISOString().split('T')[0] });
                                    setOpenCardMenuId(null);
                                  }}
                                  className="px-2 py-1 text-[10px] font-semibold rounded-md bg-surface-100 dark:bg-surface-800 hover:bg-brand-50 dark:hover:bg-brand-500/15 hover:text-brand-600 text-surface-600 dark:text-surface-300"
                                >
                                  Tomorrow
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const d = new Date();
                                    d.setDate(d.getDate() + 7);
                                    handleUpdateCardQuickField(task, { dueDate: d.toISOString().split('T')[0] });
                                    setOpenCardMenuId(null);
                                  }}
                                  className="px-2 py-1 text-[10px] font-semibold rounded-md bg-surface-100 dark:bg-surface-800 hover:bg-brand-50 dark:hover:bg-brand-500/15 hover:text-brand-600 text-surface-600 dark:text-surface-300"
                                >
                                  +7 Days
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleUpdateCardQuickField(task, { dueDate: null });
                                    setOpenCardMenuId(null);
                                  }}
                                  className="px-2 py-1 text-[10px] font-semibold rounded-md bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 hover:bg-rose-100"
                                >
                                  Clear
                                </button>
                              </div>
                            </div>
                          )}

                          {cardMenuSubView === 'labels' && (
                            <div className="p-2.5 space-y-1.5">
                              <div className="flex items-center justify-between pb-1">
                                <button onClick={() => setCardMenuSubView('main')} className="text-[11px] text-brand-600 font-bold">
                                  ← Back
                                </button>
                                <span className="text-[10px] font-bold uppercase text-surface-400">Labels</span>
                              </div>
                              <div className="space-y-1 max-h-48 overflow-y-auto">
                                {TRELLO_LABEL_PALETTE.map((pal) => {
                                  const active = (task.coloredLabels || []).some((l) => l.color === pal.color);
                                  return (
                                    <button
                                      key={pal.color}
                                      onClick={(e) => handleToggleCardLabel(e, task, pal)}
                                      className="w-full flex items-center justify-between px-2 py-1 rounded-lg hover:bg-surface-50 dark:hover:bg-surface-800"
                                    >
                                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${pal.bg}`}>
                                        {pal.name}
                                      </span>
                                      {active && <Check className="w-3.5 h-3.5 text-brand-600" />}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {cardMenuSubView === 'cover' && (
                            <div className="p-2.5 space-y-2">
                              <div className="flex items-center justify-between pb-1">
                                <button onClick={() => setCardMenuSubView('main')} className="text-[11px] text-brand-600 font-bold">
                                  ← Back
                                </button>
                                <span className="text-[10px] font-bold uppercase text-surface-400">Card Cover</span>
                              </div>
                              <div className="grid grid-cols-5 gap-1.5">
                                {TRELLO_COVER_COLORS.map((c) => (
                                  <button
                                    key={c.id}
                                    onClick={() => {
                                      handleUpdateCardQuickField(task, { coverColor: c.hex || null });
                                      setOpenCardMenuId(null);
                                    }}
                                    title={c.label}
                                    className={`h-6 rounded-lg ${c.className} border border-surface-200 dark:border-surface-700 ${
                                      (task.coverColor || '') === c.hex ? 'ring-2 ring-brand-500' : ''
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {cardMenuSubView === 'move' && (
                            <div className="p-2.5 space-y-1">
                              <div className="flex items-center justify-between pb-1">
                                <button onClick={() => setCardMenuSubView('main')} className="text-[11px] text-brand-600 font-bold">
                                  ← Back
                                </button>
                                <span className="text-[10px] font-bold uppercase text-surface-400">Move to List</span>
                              </div>
                              {effectiveColumns
                                .filter((c) => !c.isOverdue)
                                .map((destCol) => (
                                  <button
                                    key={destCol.id}
                                    onClick={() => {
                                      onUpdateTaskStatus(task.id, destCol.statusKey, destCol.isCustomList ? destCol.id : null);
                                      setOpenCardMenuId(null);
                                    }}
                                    className="w-full px-2.5 py-1.5 text-left rounded-lg hover:bg-surface-50 dark:hover:bg-surface-800 flex items-center space-x-2 text-surface-700 dark:text-surface-200"
                                  >
                                    <span className={`w-2 h-2 rounded-full ${destCol.color}`} />
                                    <span className="truncate">{destCol.title}</span>
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {colTasks.length === 0 && (
                  <div className="py-5 text-center text-[11px] text-surface-400 border border-dashed border-surface-200 dark:border-surface-800 rounded-xl">
                    No cards in {col.title}
                  </div>
                )}
              </div>

              {/* Trello Footer: "+ Add a card" button at the bottom of every list */}
              {canCreateTask && quickInputCol !== col.id && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setQuickInputCol(col.id);
                  }}
                  className="mt-1.5 w-full py-1.5 px-2.5 rounded-lg text-[11px] font-semibold text-surface-500 hover:text-surface-900 dark:hover:text-surface-100 hover:bg-surface-200/70 dark:hover:bg-surface-800/80 flex items-center space-x-1 transition-colors shrink-0"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add a card</span>
                </button>
              )}
            </div>
          );
        })}

        {/* Trello "+ Add another list" Column (When inside a Board/Project) */}
        {projectId && (
          <div className="w-[78vw] sm:w-[260px] shrink-0">
            {!isAddingList ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAddingList(true);
                }}
                className="w-full py-2.5 px-3.5 rounded-xl bg-surface-100/80 hover:bg-surface-200/80 dark:bg-surface-900/70 dark:hover:bg-surface-800 border border-dashed border-surface-300 dark:border-surface-700 text-xs font-bold text-surface-600 dark:text-surface-300 flex items-center space-x-2 transition-all shadow-2xs"
              >
                <Plus className="w-3.5 h-3.5 text-brand-600" />
                <span>Add another list</span>
              </button>
            ) : (
              <form
                onSubmit={handleAddList}
                onClick={(e) => e.stopPropagation()}
                className="p-3 rounded-xl bg-[#f4f5f7] dark:bg-[#121417] border border-brand-500/50 shadow-card space-y-2.5"
              >
                <input
                  type="text"
                  autoFocus
                  placeholder="Enter list title... (e.g. QA Testing, Blocked)"
                  value={newListTitle}
                  onChange={(e) => setNewListTitle(e.target.value)}
                  className="w-full px-3 py-2 text-xs font-semibold rounded-xl bg-white dark:bg-surface-850 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />

                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-surface-400 font-semibold">Workflow Stage:</span>
                  <select
                    value={newListStatusKey}
                    onChange={(e) => setNewListStatusKey(e.target.value as TaskStatus)}
                    className="px-2 py-1 rounded-lg bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs font-semibold"
                  >
                    <option value="backlog">Backlog</option>
                    <option value="todo">To Do</option>
                    <option value="in_progress">In Progress</option>
                    <option value="in_review">In Review</option>
                    <option value="done">Completed</option>
                  </select>
                </div>

                <div className="flex items-center space-x-1.5">
                  {LIST_DOT_COLORS.map((c) => (
                    <button
                      type="button"
                      key={c.value}
                      onClick={() => setNewListColor(c.value)}
                      className={`w-5 h-5 rounded-full ${c.value} ${newListColor === c.value ? 'ring-2 ring-offset-1 ring-brand-500' : ''}`}
                      title={c.label}
                    />
                  ))}
                </div>

                <div className="flex items-center justify-end space-x-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setIsAddingList(false)}
                    className="px-3 py-1.5 rounded-xl text-xs font-semibold text-surface-500 hover:bg-surface-200 dark:hover:bg-surface-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3.5 py-1.5 rounded-xl text-xs font-bold bg-brand-600 hover:bg-brand-700 text-white shadow-sm"
                  >
                    Add List
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>

      {pendingRequestChangesTask && (
        <RequestChangesModal
          isOpen={isRequestChangesModalOpen}
          taskTitle={pendingRequestChangesTask.title}
          taskCode={pendingRequestChangesTask.code}
          isSubmitting={isSubmittingReviewAction}
          onClose={() => {
            setIsRequestChangesModalOpen(false);
            setPendingRequestChangesTask(null);
          }}
          onSubmit={handleRequestChangesSubmit}
        />
      )}
    </div>
  );
};
